import type { IUser } from 'configcat-react';
import { useConfigCatClient } from 'configcat-react';

import { getConfig, majorVersion, minorVersion } from 'pkg/config/app';

import { isFamily, isParent } from 'pkg/api/models/membership/guards';
import { legacyRoleString } from 'pkg/api/models/membership/utils';
import {
	useCurrentAccount,
	useCurrentMembership,
	useCurrentOrganization,
} from 'pkg/identity';
import { isFamilyModeEligible } from 'pkg/identity/family';
import { useOrganizationIdentity } from 'pkg/identity/organization';
import { isAndroid, isIOS, isWeb } from 'pkg/platform';

/* Static required flags, do not edit these */
export const isDev = (): boolean => getConfig().dev;
export const isBeta = (): boolean => getConfig().beta || isDev();
export const isDevServer = (): boolean => getConfig().devServer;
export const isCypress = (): boolean => !!window.Cypress;

export enum Flag {
	SettingsDividedAddress = 'setting_divided_address',
	DisplayAds = 'display_ads',
	Tournaments = 'tournaments',
	MembershipCardsQR = 'membership_cards_qr',
	FormTypeChange = 'form_type_change',
	FormEventGroupSelectDates = 'form_event_group_select_dates',
	FormEventGroupWaitlist = 'form_event_group_waitlist_dev',
	LOKReport = 'lok_report_dev',
	ContactDuplicates = 'contact_duplicates_dev',
	WalletFritidskortet = 'wallet_fritidskortet',
	FogisV2 = 'fogis_v2',
	Ibis = 'ibis',
	SweHockey = 'swehockey',
	ProfixioHandball = 'profixio_handball',
	ProfixioBasketball = 'profixio_basketball',
	FamilyGroups = 'family_groups',
	CustomTags = 'custom_tags',
	PlayerSignupInheritance = 'player_signup_inheritance',
	Carpool = 'carpool',
	SettingsUiUpdate = 'settings_ui_update',
	PracticePlanner = 'practice_planner',
	InvoicePdf = 'invoice_pdf',
	MinutesPlayedPerGroup = 'show_minutes_played_per_group',
	AiEventImport = 'ai_event_import',
	AiUserImport = 'ai_user_import',
	AppRatingPrompt = 'app_rating_prompt',
	SchedulingEventOptionsDev = 'scheduling_event_options_dev',
	FormProductBundleUiUpdate = 'form_product_bundle_ui_update',
	ChatV2 = 'chat_v2',
	FamilyMode = 'family_mode_dev',
	FormTeamRegistrationTab = 'form_team_registration_tab',
	FkPaymentsReport = 'fk_payments_report',
	Nango = 'nango',
	Evaluations = 'evaluations',
	Announcements = 'announcements_dev',
	ClubStore = 'club_store',
	JerseyNumbers = 'jersey_numbers_dev',
	WebsiteRosters = 'website_rosters_dev',
}

/**
 * Override the value of a flag for the current session, useful for testing a feature
 * locally without going into ConfigCat and changing the flag value for everyone.
 */
export function overrideFlag(flag: string, value: boolean) {
	sessionStorage.setItem(`flagoverride:${flag}`, value.toString());
}

export function getOverride(flag: string): boolean | undefined {
	const val = sessionStorage.getItem(`flagoverride:${flag}`);
	if (val !== null) {
		return val === 'true';
	}

	return undefined;
}

export function clearAllOverrides(): void {
	const keys = Object.keys(sessionStorage).filter((k) =>
		k.startsWith('flagoverride:')
	);
	keys.forEach((k) => sessionStorage.removeItem(k));
}

function useConfigCatUser(): IUser {
	const acc = useCurrentAccount();
	const org = useCurrentOrganization();
	const membership = useCurrentMembership();

	const custom: Record<string, string | number> = {
		platform_web: String(isWeb()),
		platform_ios: String(isIOS()),
		platform_android: String(isAndroid()),
		major_version: majorVersion(),
		minor_version: minorVersion(),
	};
	let identifier = 'anonymous';

	if (org?.id) {
		identifier = String(org.id);
		custom.org_id = String(org.id);
		custom.org_name = org.name;
		if (org.createdAt) {
			custom.org_created_at = Number(org.createdAt);
		}
	}

	if (acc?.id) {
		identifier = String(acc.id);
		custom.account_id = String(acc.id);
	}

	if (membership?.groupId) {
		custom.membership_group_id = String(membership.groupId);
		custom.membership_role = legacyRoleString(membership);
		custom.membership_is_parent = String(isParent(membership));
		custom.membership_is_family = String(isFamily(membership));
	}

	const user: IUser = { identifier, custom };

	if (acc?.email && isDev()) {
		user.email = acc.email;
	}

	return user;
}

/**
 * Reads a feature flag from ConfigCat synchronously via snapshot. Session-storage
 * overrides win, otherwise the value comes from the cached ConfigCat snapshot.
 * The FlagProvider awaits `client.waitForReady()` before rendering, so the snapshot
 * is populated by the time any consumer calls this hook — no async/default flicker.
 */
export function useFlag<T>(flag: Flag): T {
	const override = getOverride(flag);
	const client = useConfigCatClient();
	const ccUser = useConfigCatUser();

	if (override !== undefined) return override as T;
	return client.snapshot().getValue(flag, false, ccUser) as unknown as T;
}

/**
 * Family billing groups (M1) are on in dev builds so the feature works locally
 * without configuring ConfigCat; otherwise reads the family_groups rollout flag.
 * Mirrors familygroupsflag.Enabled on the backend.
 */
export function useFamilyGroupsEnabled(): boolean {
	const enabled = useFlag<boolean>(Flag.FamilyGroups);

	return isDev() || enabled;
}

/**
 * Carpooling is on in dev builds so the feature works locally without
 * configuring ConfigCat; otherwise reads the carpool rollout flag.
 */
export function useCarpoolEnabled(): boolean {
	const enabled = useFlag<boolean>(Flag.Carpool);

	return isDev() || enabled;
}

/**
 * Per-group minutes played is on in dev builds so the feature works locally
 * without configuring ConfigCat; otherwise reads the rollout flag.
 */
export function useMinutesPlayedPerGroupEnabled(): boolean {
	const enabled = useFlag<boolean>(Flag.MinutesPlayedPerGroup);

	return isDev() || enabled;
}

/**
 * Family mode lets a parent with two or more children see them all in one
 * aggregated context instead of switching per child and group. On in dev
 * builds so it works locally without configuring ConfigCat, mirroring the
 * backend's env.IsLocal() fallback.
 */
export function useFamilyModeAvailable(): boolean {
	const enabled = useFlag<boolean>(Flag.FamilyMode);
	const { wards } = useOrganizationIdentity();

	return (isDev() || enabled) && isFamilyModeEligible(wards);
}
